import type { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../../components/Dashboard/CommonDashboardHeader";
import { InHouseLineChart } from "../../../components/UIToolkit/Graphs/LineChart/InHouseLineChart";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { ALPHA, CHART_REFERENCE } from "../../../theme/tokens";
import type { ManagementCenterHPPDResponse } from "../../../utils/models";
import { type ClampRange, fetchedDataToChartDataGroup, getCensusYDomain } from "../utils";

type Props = {
	data?: ManagementCenterHPPDResponse;
	isLoading: boolean;
	trailing?: ReactNode;
	containerSx?: SxProps<Theme>;
	shrinkLegendForSmallScreens?: boolean;
	/** Selected range; clamps edge weekly buckets so the chart never plots a point outside the selection. */
	clampRange?: ClampRange;
};

export const HPPDOverTime: FC<Props> = ({ data, isLoading, trailing, containerSx, shrinkLegendForSmallScreens, clampRange }) => {
	const { timezone, chosenDepartment } = useHospitalData();
	const navigate = useNavigate();
	const { track } = useAnalytics();
	const [searchParams] = useSearchParams();

	const handleClick = () => {
		const params = new URLSearchParams(searchParams);
		params.set("metric", "hppd");
		track("schedule_vs_actual_report_opened", { metric: "hppd", department_id: chosenDepartment.department_id });
		navigate(`/${chosenDepartment.department_id}/schedule-vs-actual?${params.toString()}`);
	};

	const hasTarget = data && Object.keys(data.targetHppd).length > 0;

	const transformedDataLeft =
		!isLoading && data
			? [
					fetchedDataToChartDataGroup({
						data: data.hppdScheduled,
						name: "SCHEDULED HPPD",
						timezone,
						clampRange,
						color: CHART_REFERENCE.SCHEDULED,
					}),
					fetchedDataToChartDataGroup({
						data: data.hppdActual,
						name: "ACTUAL HPPD",
						timezone,
						clampRange,
						color: CHART_REFERENCE.ACTUAL,
					}),
					...(hasTarget
						? [
								fetchedDataToChartDataGroup({
									data: data.targetHppd,
									name: "TARGET",
									timezone,
									clampRange,
									color: CHART_REFERENCE.TARGET,
									strokeDasharray: CHART_REFERENCE.TARGET_DASH,
								}),
							]
						: []),
				]
			: undefined;

	const transformedDataRight =
		!isLoading && data && transformedDataLeft
			? [
					fetchedDataToChartDataGroup({
						data: data.census,
						name: "CENSUS",
						timezone,
						clampRange,
						color: CHART_REFERENCE.CENSUS,
					}),
				]
			: undefined;

	const targetValues = hasTarget ? Object.values(data.targetHppd) : [];
	const maxHPPD = data ? Math.max(...Object.values(data.hppdActual), ...Object.values(data.hppdScheduled), ...targetValues) + 0.1 : 0;
	const minHPPD = data ? Math.max(Math.floor(Math.min(...Object.values(data.hppdActual), ...Object.values(data.hppdScheduled), ...targetValues) - 0.1), 0) : 0;
	const censusDomain = getCensusYDomain(data?.census);

	return (
		<CommonDashboardBox
			onClick={handleClick}
			sx={
				((theme) => ({
					display: "flex",
					flexDirection: "column",
					alignItems: "start",
					gap: 1,
					width: "calc(33% - 4px)",
					...containerSx,
					cursor: "pointer",
					"&:hover": { boxShadow: `0px 2px 12px 0px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}` },
					transition: "box-shadow 0.2s",
				})) as SxProps<Theme>
			}
		>
			<CommonDashboardHeader label={"HPPD Over Time"} trailing={trailing} />
			<InHouseLineChart
				shrinkLegendForSmallScreens={shrinkLegendForSmallScreens}
				width={"100%"}
				height={containerSx ? "100%" : undefined}
				leftAxisData={
					transformedDataLeft
						? {
								dataGroups: transformedDataLeft,
								maxValue: maxHPPD,
								minValue: minHPPD,
								tickRenderer: (value) => {
									return `${Number.parseFloat(value.toFixed(1))}`;
								},
							}
						: undefined
				}
				rightAxisData={
					transformedDataRight
						? {
								dataGroups: transformedDataRight,
								maxValue: censusDomain?.max,
								minValue: censusDomain?.min,
								tickRenderer: (value) => {
									return `${value}${value === censusDomain?.max ? "🧑" : ""}`;
								},
							}
						: undefined
				}
				tooltipSortOrder={{ "SCHEDULED HPPD": 0, "ACTUAL HPPD": 1, TARGET: 2, CENSUS: 3 }}
			/>
		</CommonDashboardBox>
	);
};
